import { Platform } from 'react-native';

import { BUDGET_API_URL } from '../config/environment';

export type ApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: string;
  /** Override API host (default: BUDGET_API_URL). */
  baseUrl?: string;
  /** Skip 401 → refresh → retry (used by /auth/refresh itself). */
  skipAuthRefresh?: boolean;
};

export class ApiError extends Error {
  statusCode?: number;
  attemptsRemaining?: number;
  resetAt?: number;
  upgradeRequired?: boolean;
  monthlyLimitReached?: boolean;
  remaining?: number;
  limit?: number;
  periodResetsAt?: string;

  constructor(
    message: string,
    statusCode?: number,
    extras?: {
      attemptsRemaining?: number;
      resetAt?: number;
      upgradeRequired?: boolean;
      monthlyLimitReached?: boolean;
      remaining?: number;
      limit?: number;
      periodResetsAt?: string;
    },
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.attemptsRemaining = extras?.attemptsRemaining;
    this.resetAt = extras?.resetAt;
    this.upgradeRequired = extras?.upgradeRequired;
    this.monthlyLimitReached = extras?.monthlyLimitReached;
    this.remaining = extras?.remaining;
    this.limit = extras?.limit;
    this.periodResetsAt = extras?.periodResetsAt;
  }
}

/** Analyze quota / upload limit from POST /imports or /imports/portfolio. */
export function isAnalyzeQuotaError(error: unknown): error is ApiError {
  if (!(error instanceof ApiError) || error.statusCode !== 403) return false;
  return error.upgradeRequired === true || error.monthlyLimitReached === true;
}

/** Result of attempting /auth/refresh — callers must only clear session on `auth`. */
export type TokenRefreshResult =
  | { ok: true }
  | { ok: false; reason: 'auth' | 'network' | 'no_token' };

export type ApiAuthHandlers = {
  getAccessToken: () => Promise<string | null>;
  refreshTokens: () => Promise<TokenRefreshResult>;
  /** Only invoked when refresh confirmed the session is invalid (401 / invalid refresh). */
  onRefreshFailed?: (reason: 'refresh_401') => void | Promise<void>;
};

let authHandlers: ApiAuthHandlers | null = null;
let refreshInFlight: Promise<TokenRefreshResult> | null = null;

export function configureApiAuth(handlers: ApiAuthHandlers | null): void {
  authHandlers = handlers;
}

export const isAuthFailureStatus = (status: number | undefined, message: string): boolean => {
  if (status === 401) return true;
  if (status !== 403) return false;
  const lower = message.toLowerCase();
  return (
    lower.includes('token') ||
    lower.includes('unauthorized') ||
    lower.includes('authentication') ||
    lower.includes('expired') ||
    lower.includes('account has been deleted')
  );
};

/** Map a thrown refresh error to auth vs transient/network — never treat offline as logout. */
export function classifyRefreshFailure(error: unknown): 'auth' | 'network' {
  if (error instanceof ApiError) {
    if (isAuthFailureStatus(error.statusCode, error.message)) return 'auth';
    // No status / 5xx / other HTTP → keep session (offline, CORS, gateway blips).
    return 'network';
  }
  return 'network';
}

const singleFlightRefresh = async (): Promise<TokenRefreshResult> => {
  if (!authHandlers) return { ok: false, reason: 'no_token' };
  if (!refreshInFlight) {
    refreshInFlight = authHandlers
      .refreshTokens()
      .catch((error: unknown): TokenRefreshResult => ({
        ok: false,
        reason: classifyRefreshFailure(error),
      }))
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

const clearSessionIfAuthRefreshFailed = async (
  result: TokenRefreshResult,
): Promise<boolean> => {
  if (result.ok) return false;
  if (result.reason === 'auth') {
    await authHandlers?.onRefreshFailed?.('refresh_401');
    return true;
  }
  if (result.reason === 'network') {
    console.info('[apiClient] network_kept_session', { context: 'token_refresh' });
  }
  return false;
};

function parseRetryAfterMs(response: Response): number | undefined {
  const raw = response.headers.get('Retry-After') ?? response.headers.get('retry-after');
  if (!raw) return undefined;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const when = Date.parse(raw);
  if (Number.isFinite(when)) return Math.max(0, when - Date.now());
  return undefined;
}

function isHttpRateLimited(status: number, message: string): boolean {
  if (status === 429) return true;
  if (status !== 403) return false;
  const lower = message.toLowerCase();
  // WAF rate rule often returns 403; don't treat auth 403s as throttle.
  if (isAuthFailureStatus(status, message)) return false;
  return (
    lower.includes('rate') ||
    lower.includes('throttl') ||
    lower.includes('too many') ||
    lower.includes('limit exceeded')
  );
}

function isRetryableServerStatus(status: number): boolean {
  return status === 500 || status === 502 || status === 503 || status === 504;
}

/** API Gateway / Lambda crash copy is useless in the UI. */
export function friendlyApiErrorMessage(message: string, status?: number): string {
  const lower = message.toLowerCase().trim();
  if (
    status === 504 ||
    lower.includes('timed out') ||
    lower.includes('timeout') ||
    lower.includes('endpoint request timed out')
  ) {
    return 'This is taking longer than usual. Pull to refresh.';
  }
  if (status === 502 || status === 503) {
    return 'Couldn’t load this right now. Pull to refresh.';
  }
  if (status === 500 || lower.includes('internal server error')) {
    // Keep Stripe / billing copy — only hide empty gateway junk.
    if (
      !lower ||
      lower === 'internal server error' ||
      lower === 'http 500' ||
      lower.startsWith('http 500')
    ) {
      return 'Couldn’t load this right now. Pull to refresh.';
    }
    return message;
  }
  if (
    lower === 'failed to fetch' ||
    lower === 'network request failed' ||
    lower.includes('network request failed')
  ) {
    return 'Couldn’t reach the server. Check your connection and retry.';
  }
  return message;
}

function throwIfRateLimited(
  status: number,
  message: string,
  retryAfterMs?: number,
): void {
  if (!isHttpRateLimited(status, message)) return;
  throw new ApiError('Too many requests. Wait a moment, then refresh.', status, {
    resetAt: retryAfterMs != null ? Date.now() + retryAfterMs : undefined,
  });
}

function resolveApiUrl(endpoint: string, baseUrl = BUDGET_API_URL): string {
  const root = baseUrl.replace(/\/$/, '');
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${root}${path}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withoutAuthHeader(options: ApiRequestOptions): ApiRequestOptions {
  const restHeaders = { ...(options.headers ?? {}) };
  delete restHeaders.Authorization;
  return { ...options, headers: restHeaders };
}

async function apiRequestWithToken<T>(
  endpoint: string,
  options: ApiRequestOptions,
  accessToken: string | null,
): Promise<T> {
  const rest = withoutAuthHeader(options);
  return apiRequest<T>(
    endpoint,
    {
      ...rest,
      headers: {
        ...rest.headers,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    },
    false,
  );
}

function buildRequestHeaders(
  options: ApiRequestOptions,
  accessToken: string | null,
): Record<string, string> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = { ...(options.headers ?? {}) };
  const hasBody = options.body != null && options.body !== '';
  // JSON Content-Type on GET forces a CORS preflight and some WAFs 403 it
  // without ACAO — the browser then reports TypeError "Failed to fetch".
  if (hasBody || method === 'POST' || method === 'PUT' || method === 'PATCH') {
    if (!headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }
  }
  if (!headers.Authorization && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

export async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {},
  allowRetry = true,
  attempt = 1,
): Promise<T> {
  const url = resolveApiUrl(endpoint, options.baseUrl ?? BUDGET_API_URL);

  const accessToken = options.headers?.Authorization
    ? null
    : authHandlers
      ? await authHandlers.getAccessToken()
      : null;
  const headers = buildRequestHeaders(options, accessToken);

  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body: options.body,
    });
  } catch (networkError) {
    // Cross-origin 401/403 without ACAO, cold TLS, and login bursts all surface
    // as TypeError "Failed to fetch". Retry the same token first — a fresh login
    // already has a valid access token; forcing /auth/refresh first often fails
    // the same way and never retries the original GET.
    if (allowRetry) {
      try {
        await sleep(350);
        const retryToken = authHandlers ? await authHandlers.getAccessToken() : accessToken;
        return await apiRequestWithToken<T>(endpoint, options, retryToken);
      } catch {
        const shouldAttemptRefresh = !options.skipAuthRefresh && !!authHandlers;
        if (shouldAttemptRefresh) {
          const refreshResult = await singleFlightRefresh();
          if (refreshResult.ok) {
            const newAccessToken = await authHandlers!.getAccessToken();
            return await apiRequestWithToken<T>(endpoint, options, newAccessToken);
          }
          await clearSessionIfAuthRefreshFailed(refreshResult);
        }
      }
    }
    const detail =
      networkError instanceof Error ? networkError.message : 'Network request failed';
    throw new ApiError(friendlyApiErrorMessage(detail));
  }

  const text = await response.text();
  let data: Record<string, unknown> = {};
  if (text) {
    try {
      data = JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw new ApiError(`Invalid JSON response (HTTP ${response.status})`, response.status);
    }
  }

  if (!response.ok) {
    const rawMessage =
      (data.error as string) || (data.message as string) || `HTTP ${response.status}`;
    const message = friendlyApiErrorMessage(rawMessage, response.status);

    throwIfRateLimited(response.status, rawMessage, parseRetryAfterMs(response));

    const method = (options.method ?? 'GET').toUpperCase();
    const shouldRetryServer =
      allowRetry &&
      method === 'GET' &&
      isRetryableServerStatus(response.status) &&
      attempt < 3;
    if (shouldRetryServer) {
      // Cold Lambdas often 500 on the first hit and succeed once Mongo is warm.
      // Don't stampede a second cold start — wait, then retry the same function.
      await sleep(attempt === 1 ? 1100 : 2200);
      return apiRequest<T>(endpoint, options, allowRetry, attempt + 1);
    }

    const shouldAttemptRefresh =
      allowRetry &&
      !options.skipAuthRefresh &&
      !!authHandlers &&
      isAuthFailureStatus(response.status, rawMessage);

    if (shouldAttemptRefresh) {
      const refreshResult = await singleFlightRefresh();
      if (refreshResult.ok) {
        const newAccessToken = await authHandlers!.getAccessToken();
        const restHeaders = { ...(options.headers ?? {}) };
        delete restHeaders.Authorization;
        return apiRequest<T>(
          endpoint,
          {
            ...options,
            headers: {
              ...restHeaders,
              ...(newAccessToken ? { Authorization: `Bearer ${newAccessToken}` } : {}),
            },
          },
          false,
        );
      }

      await clearSessionIfAuthRefreshFailed(refreshResult);
    }

    throw new ApiError(message, response.status, {
      attemptsRemaining: data.attemptsRemaining as number | undefined,
      resetAt: data.resetAt as number | undefined,
      upgradeRequired: data.upgradeRequired === true,
      monthlyLimitReached: data.monthlyLimitReached === true,
      remaining: typeof data.remaining === 'number' ? data.remaining : undefined,
      limit: typeof data.limit === 'number' ? data.limit : undefined,
      periodResetsAt: typeof data.periodResetsAt === 'string' ? data.periodResetsAt : undefined,
    });
  }

  return data as T;
}

export type SseJsonEvent = Record<string, unknown>;

export type StreamSseOptions = {
  method?: 'POST' | 'GET';
  headers?: Record<string, string>;
  body?: string;
  baseUrl?: string;
  signal?: AbortSignal;
  /** Called for each parsed `data:` JSON payload. */
  onEvent: (event: SseJsonEvent) => void;
};

/**
 * POST/GET a text/event-stream endpoint and parse SSE `data:` lines as JSON.
 * Uses fetch ReadableStream when available; falls back to XHR progressive reads (RN).
 */
export async function streamSseRequest(
  endpoint: string,
  options: StreamSseOptions,
  allowRetry = true,
): Promise<void> {
  const url = resolveApiUrl(endpoint, options.baseUrl ?? BUDGET_API_URL);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    ...(options.headers ?? {}),
  };

  if (!headers.Authorization && authHandlers) {
    const accessToken = await authHandlers.getAccessToken();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  const method = options.method ?? 'POST';

  const parseSseBuffer = (raw: string, carry: { buf: string }): void => {
    carry.buf += raw;
    const parts = carry.buf.split('\n');
    carry.buf = parts.pop() ?? '';
    for (const line of parts) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      const payload = trimmed.slice(5).trim();
      if (!payload || payload === '[DONE]') continue;
      try {
        options.onEvent(JSON.parse(payload) as SseJsonEvent);
      } catch {
        // ignore malformed chunks
      }
    }
  };

  const handleAuthFailure = async (status: number, message: string): Promise<boolean> => {
    if (!allowRetry || !authHandlers || !isAuthFailureStatus(status, message)) return false;
    const refreshResult = await singleFlightRefresh();
    if (!refreshResult.ok) {
      await clearSessionIfAuthRefreshFailed(refreshResult);
      return false;
    }
    const newAccessToken = await authHandlers.getAccessToken();
    const restHeaders = { ...(options.headers ?? {}) };
    delete restHeaders.Authorization;
    await streamSseRequest(
      endpoint,
      {
        ...options,
        headers: {
          ...restHeaders,
          ...(newAccessToken ? { Authorization: `Bearer ${newAccessToken}` } : {}),
        },
      },
      false,
    );
    return true;
  };

  const streamViaXhr = (): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.responseType = 'text';

      const carry = { buf: '' };
      let lastIndex = 0;

      const onAbort = () => {
        xhr.abort();
        reject(new ApiError('Aborted'));
      };
      options.signal?.addEventListener('abort', onAbort);

      xhr.onprogress = () => {
        const text = xhr.responseText ?? '';
        if (text.length <= lastIndex) return;
        const chunk = text.slice(lastIndex);
        lastIndex = text.length;
        parseSseBuffer(chunk, carry);
      };

      xhr.onerror = () => {
        options.signal?.removeEventListener('abort', onAbort);
        reject(new ApiError('Network request failed'));
      };

      xhr.onload = () => {
        options.signal?.removeEventListener('abort', onAbort);
        const text = xhr.responseText ?? '';
        if (text.length > lastIndex) {
          parseSseBuffer(text.slice(lastIndex), carry);
          lastIndex = text.length;
        }
        if (carry.buf.trim()) parseSseBuffer('\n', carry);

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }

        let message = `HTTP ${xhr.status}`;
        try {
          const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
          message = (data.error as string) || (data.message as string) || message;
        } catch {
          /* keep default */
        }

        try {
          const retryRaw = xhr.getResponseHeader('Retry-After');
          const retryAfterMs =
            retryRaw && Number.isFinite(Number(retryRaw))
              ? Number(retryRaw) * 1000
              : undefined;
          throwIfRateLimited(xhr.status, message, retryAfterMs);
        } catch (rateError) {
          reject(rateError);
          return;
        }

        void handleAuthFailure(xhr.status, message).then((retried) => {
          if (retried) {
            resolve();
            return;
          }
          reject(new ApiError(friendlyApiErrorMessage(message, xhr.status), xhr.status));
        });
      };

      xhr.send(options.body ?? null);
    });

  // Native: XHR progressive reads (fetch often buffers). Web: fetch ReadableStream.
  const preferXhr = Platform.OS !== 'web' && typeof XMLHttpRequest !== 'undefined';

  if (preferXhr) {
    await streamViaXhr();
    return;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: options.body,
      signal: options.signal,
    });
  } catch (networkError) {
    if (allowRetry && authHandlers) {
      const refreshResult = await singleFlightRefresh();
      if (refreshResult.ok) {
        const newAccessToken = await authHandlers.getAccessToken();
        const restHeaders = { ...(options.headers ?? {}) };
        delete restHeaders.Authorization;
        await streamSseRequest(
          endpoint,
          {
            ...options,
            headers: {
              ...restHeaders,
              ...(newAccessToken ? { Authorization: `Bearer ${newAccessToken}` } : {}),
            },
          },
          false,
        );
        return;
      }
      await clearSessionIfAuthRefreshFailed(refreshResult);
    }
    const detail =
      networkError instanceof Error ? networkError.message : 'Network request failed';
    throw new ApiError(friendlyApiErrorMessage(detail));
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    let message = `HTTP ${response.status}`;
    try {
      const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
      message = (data.error as string) || (data.message as string) || message;
    } catch {
      if (text) message = text.slice(0, 200);
    }
    throwIfRateLimited(response.status, message, parseRetryAfterMs(response));
    if (await handleAuthFailure(response.status, message)) return;
    throw new ApiError(friendlyApiErrorMessage(message, response.status), response.status);
  }

  type StreamReader = {
    read: () => Promise<{ done: boolean; value?: Uint8Array }>;
  };
  const body = (response as Response & { body?: { getReader?: () => StreamReader } }).body;
  const reader = body?.getReader?.();
  if (reader) {
    const decodeSseChunk = (value: Uint8Array): string => {
      // Prefer TextDecoder when present (web); otherwise latin1-safe fallback for ASCII SSE.
      const Decoder = (globalThis as { TextDecoder?: new () => { decode: (v: Uint8Array) => string } })
        .TextDecoder;
      if (Decoder) return new Decoder().decode(value);
      let out = '';
      for (let i = 0; i < value.length; i += 1) out += String.fromCharCode(value[i]!);
      return out;
    };
    const carry = { buf: '' };
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) parseSseBuffer(decodeSseChunk(value), carry);
    }
    if (carry.buf.trim()) parseSseBuffer('\n', carry);
    return;
  }

  // No stream reader — parse buffered body once (do not re-request).
  const buffered = await response.text();
  const carry = { buf: '' };
  parseSseBuffer(buffered, carry);
  if (carry.buf.trim()) parseSseBuffer('\n', carry);
}
